import { createContext, ReactNode, useContext, useMemo } from 'react';
import { uniqueId } from 'lodash-es';
import { StoreName } from '../../constants';
import { createHookStore } from '../createHookStore';

interface CreateDynamicHookStoreProps<State, Actions> {
  storeName: StoreName;
  initialState: State;
  actionsCreator: (
    useStore: ReturnType<typeof createHookStore<State>>
  ) => Actions;
}

export function createDynamicHookStore<State, Actions>({
  storeName,
  initialState,
  actionsCreator
}: CreateDynamicHookStoreProps<State, Actions>) {
  const Context = createContext<{
    useStore: ReturnType<typeof createHookStore<State>>;
    actions: Actions;
  }>({
    useStore: createHookStore(storeName, initialState),
    actions: {} as Actions
  });

  const Provider = ({ children }: { children: ReactNode }) => {
    const store = useMemo(() => {
      // todo SSR: make this name accessible for SSR
      const uniqueStoreName = uniqueId(storeName) as StoreName; // uniqueId returns string, not more strict type
      const useBoundStore = createHookStore<State>(
        uniqueStoreName,
        initialState
      );
      const boundActions = actionsCreator(useBoundStore);

      return { actions: boundActions, useStore: useBoundStore };
    }, []);

    return <Context.Provider value={store}>{children}</Context.Provider>;
  };

  const useCtxStore = (
    ...args: Parameters<ReturnType<typeof createHookStore<State>>>
  ) => {
    const context = useContext(Context);
    return context.useStore(...args);
  };

  const useCtxActions = () => useContext(Context).actions;

  return {
    Context,
    Provider,
    useCtxStore,
    useCtxActions
  };
}
